import { FunctionComponent, JSX, useEffect, useState } from 'react'
import { MediaFile, MediaType } from '@/types/NewsFeed'
import 'swiper/swiper-bundle.css'
import './swiper.scss'
import { Swiper, SwiperSlide } from 'swiper/react'
import { FreeMode, Navigation, Pagination, Thumbs } from 'swiper/modules'
import styles from './mediaContainer.module.scss'
import { Dialog } from '@mui/material'
import { Close, Download } from '@mui/icons-material'
import Mobile from '@/modules/desktopMobile/mobile'
import DescriptionIcon from '@mui/icons-material/Description'
import Desktop from '@/modules/desktopMobile/desktop'
import { FileOpener, FileOpenerOptions } from '@capacitor-community/file-opener'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { Share } from '@capacitor/share'
import { Swiper as SwiperType } from 'swiper/types'
import Bugsnag, { NotifiableError } from '@bugsnag/js'
import Loading from '@/components/Loading/Loading.tsx'
import { useLazyQuery } from '@apollo/client'
import { GET_MEDIA } from '@/modules/API/news/news.ts'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon'

interface MediaContainerProps {
    mediaList: Array<MediaFile> | null
    isApplication: boolean
    daycareId: number
    postId: number
}

const MediaContainer: FunctionComponent<MediaContainerProps> = ({
    mediaList,
    isApplication,
    daycareId,
    postId
}: MediaContainerProps) => {
    const [isFullScreen, setIsFullScreen] = useState(false)
    const [selectedImage, setSelectedImage] = useState<number>(0)
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null)

    const mediaSetList: Set<MediaType> = new Set(
        mediaList?.map((mediaFile: MediaFile): MediaType => mediaFile.media.type)
    )
    const mediaType: MediaType | null = mediaSetList.size === 1 ? mediaSetList.values().next().value || null : null

    const [mediaListSorted, setMediaListSorted] = useState<Array<MediaFile>>([])
    const [mediaBase64Map, setMediaBase64Map] = useState<Record<number, string>>({})

    const [getMediaBase64] = useLazyQuery(GET_MEDIA)

    const mediaKey: string = mediaList?.map((m: MediaFile): number => m.media.id).join(',') ?? ''

    useEffect(() => {
        setMediaBase64Map({})

        if (!mediaList || mediaList.length === 0) {
            setMediaListSorted([])
            return
        }

        const sorted: MediaFile[] = [...mediaList].sort((a: MediaFile, b: MediaFile) => a.media.id - b.media.id)
        setMediaListSorted(sorted)

        let cancelled: boolean = false

        sorted.forEach((mediaFile: MediaFile): void => {
            getMediaBase64({
                variables: {
                    daycareId,
                    postId,
                    mediaId: mediaFile.media.id
                }
            })
                .then((result): void => {
                    if (!cancelled && result.data?.getMedia) {
                        setMediaBase64Map(
                            (prev: Record<number, string>): Record<number, string> => ({
                                ...prev,
                                [mediaFile.media.id]: result.data.getMedia.base64Data
                            })
                        )
                    }
                })
                .catch((error: NotifiableError): void => {
                    Bugsnag.notify(error)
                })
        })

        return () => {
            cancelled = true
        }
    }, [postId, daycareId, mediaKey])

    function handleImageClick(index: number): void {
        setIsFullScreen(true)
        setSelectedImage(index)
    }

    function handleCloseFullScreen(): void {
        setIsFullScreen(false)
        setSelectedImage(0)
    }

    function openDocument(base64Data: string): void {
        const byteCharacters: string = atob(base64Data)
        const byteNumbers: number[] = new Array(byteCharacters.length)
        for (let i: number = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray: Uint8Array<ArrayBuffer> = new Uint8Array(byteNumbers)
        const blob: Blob = new Blob([byteArray], { type: 'application/pdf' })
        const url: string = URL.createObjectURL(blob)
        window.open(url, '_blank')
    }

    async function mobileOpenDocument(docName: string, base64Data: string): Promise<void> {
        try {
            const { uri } = await Filesystem.writeFile({
                path: docName,
                data: base64Data,
                directory: Directory.Cache
            })
            const fileOpenerOptions: FileOpenerOptions = {
                filePath: uri,
                contentType: 'application/pdf',
                openWithDefault: true
            }
            await FileOpener.open(fileOpenerOptions)
        } catch (error) {
            Bugsnag.notify(error as NotifiableError)
        }
    }

    function base64ToBlob(base64Data: string): Blob {
        const byteCharacters: string = atob(base64Data)
        const byteNumbers: number[] = new Array(byteCharacters.length)
        for (let i: number = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray: Uint8Array<ArrayBuffer> = new Uint8Array(byteNumbers)
        return new Blob([byteArray], { type: 'application/octet-stream' })
    }

    function downloadOnWeb(fileName: string, base64Data: string): void {
        const blob: Blob = base64ToBlob(base64Data)
        const url: string = URL.createObjectURL(blob)

        const link: HTMLAnchorElement = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        URL.revokeObjectURL(url)
    }

    async function downloadOnMobile(fileName: string, base64Data: string): Promise<void> {
        try {
            const { uri } = await Filesystem.writeFile({
                path: fileName,
                data: base64Data,
                directory: Directory.Cache
            })
            await Share.share({
                files: [uri],
                dialogTitle: fileName
            })
        } catch (error) {
            Bugsnag.notify(error as NotifiableError)
        }
    }

    async function handleDownload(fileName: string, base64Data: string): Promise<void> {
        if (isApplication) {
            await downloadOnMobile(fileName, base64Data)
        } else {
            downloadOnWeb(fileName, base64Data)
        }
    }

    function renderSwiper(isFullScreen: boolean): JSX.Element {
        return (
            <Swiper
                id={isFullScreen ? 'mainFullScreen' : 'main'}
                loop={mediaListSorted.length > 1}
                spaceBetween={10}
                navigation={true}
                thumbs={isFullScreen ? undefined : { swiper: thumbsSwiper }}
                modules={isFullScreen ? [FreeMode, Navigation] : [FreeMode, Navigation, Thumbs]}
                className={isFullScreen ? styles.FullScreenSwiper : styles.swiper}
                initialSlide={selectedImage}
            >
                {mediaListSorted.map((mediaFile: MediaFile, index: number) => {
                    const base64: string = mediaBase64Map[mediaFile.media.id]
                    if (!base64)
                        return (
                            <SwiperSlide key={mediaFile.media.id}>
                                <Loading />
                            </SwiperSlide>
                        )

                    return (
                        <SwiperSlide key={mediaFile.media.id} className={styles.imgContainer}>
                            <div className={styles.imgViewer}>
                                <img
                                    src={`data:image/png;base64,${base64}`}
                                    alt={mediaFile.media.fileName}
                                    className={isFullScreen ? styles.fullScreenImage : styles.renderImage}
                                    onClick={() => !isFullScreen && handleImageClick(index)}
                                />
                                {mediaFile.media.canDownload && (
                                    <ButtonIcon
                                        icon={<Download />}
                                        onClick={() => handleDownload(mediaFile.media.fileName, base64)}
                                        className={styles.DownloadButton}
                                        parent
                                    />
                                )}
                            </div>
                        </SwiperSlide>
                    )
                })}
            </Swiper>
        )
    }

    return (
        <div className={styles.MediaContainer}>
            {mediaType === MediaType.IMAGE && (
                <>
                    {renderSwiper(false)}
                    {mediaListSorted.length > 1 && (
                        <Swiper
                            id={'thumbs'}
                            onSwiper={setThumbsSwiper}
                            spaceBetween={10}
                            slidesPerView={5}
                            modules={[FreeMode, Navigation, Thumbs]}
                            className={styles.thumbsSwiper}
                            initialSlide={selectedImage}
                            wrapperClass={styles.swiperWrapper}
                        >
                            {mediaListSorted.map((mediaFile: MediaFile) => {
                                const base64: string = mediaBase64Map[mediaFile.media.id]
                                if (!base64)
                                    return (
                                        <SwiperSlide key={mediaFile.media.id}>
                                            <Loading size={'small'} />
                                        </SwiperSlide>
                                    )

                                return (
                                    <SwiperSlide className={styles.imgContainer} key={mediaFile.media.id}>
                                        <img
                                            src={`data:image/png;base64,${base64}`}
                                            alt={mediaFile.media.fileName}
                                            className={styles.renderImage}
                                        />
                                    </SwiperSlide>
                                )
                            })}
                        </Swiper>
                    )}
                    {isFullScreen && (
                        <Dialog
                            open={isFullScreen}
                            onClose={handleCloseFullScreen}
                            fullWidth
                            maxWidth='lg'
                            className={styles.DialogFullScreen}
                            fullScreen={true}
                            slotProps={{
                                paper: {
                                    style: {
                                        backgroundColor: 'black',
                                        padding: 0,
                                        margin: 0,
                                        boxShadow: 'none',
                                        display: 'flex',
                                        justifyContent: 'center'
                                    }
                                }
                            }}
                        >
                            {renderSwiper(true)}
                            <a onClick={handleCloseFullScreen} className={styles.closeButton}>
                                <Close />
                            </a>
                        </Dialog>
                    )}
                </>
            )}
            {mediaType === MediaType.VIDEO &&
                mediaList &&
                mediaList.length > 0 &&
                (() => {
                    const videoMedia: MediaFile = mediaList[0]
                    const base64: string | undefined = mediaBase64Map[videoMedia.media.id]
                    if (!base64) {
                        return <Loading />
                    }

                    return (
                        <div className={styles.videoContainer}>
                            <div className={styles.videoViewer}>
                                <video className={styles.video} controls>
                                    <source src={`data:video/mp4;base64,${base64}`} type='video/mp4' />
                                </video>
                                {videoMedia.media.canDownload && (
                                    <ButtonIcon
                                        icon={<Download />}
                                        onClick={() => handleDownload(videoMedia.media.fileName, base64)}
                                        className={styles.DownloadButton}
                                        parent
                                    />
                                )}
                            </div>
                        </div>
                    )
                })()}
            {mediaType === MediaType.PDF && (
                <Swiper
                    id={'pdf'}
                    pagination={true}
                    loop={mediaListSorted.length > 1}
                    spaceBetween={10}
                    navigation={true}
                    slidesPerView={1}
                    modules={[FreeMode, Navigation, Pagination]}
                    className={styles.swiper}
                >
                    {mediaListSorted.map((mediaFile: MediaFile) => {
                        const base64: string = mediaBase64Map[mediaFile.media.id]
                        if (!base64)
                            return (
                                <SwiperSlide key={mediaFile.media.id}>
                                    <Loading size={'small'} />
                                </SwiperSlide>
                            )

                        return (
                            <SwiperSlide key={mediaFile.media.id} className={styles.swiperSlide}>
                                <div className={styles.documentViewer}>
                                    <Mobile>
                                        <a
                                            target='_blank'
                                            rel='noreferrer'
                                            className={styles.documentContainer}
                                            onClick={async () =>
                                                isApplication
                                                    ? await mobileOpenDocument(mediaFile.media.fileName, base64)
                                                    : openDocument(base64)
                                            }
                                        >
                                            <DescriptionIcon className={styles.documentIcon} />
                                            <p> {mediaFile.media.fileName ? mediaFile.media.fileName : 'Document'} </p>
                                        </a>
                                    </Mobile>
                                    <Desktop>
                                        <a onClick={() => openDocument(base64)} className={styles.documentContainer}>
                                            <DescriptionIcon className={styles.documentIcon} />
                                            <p> {mediaFile.media.fileName ? mediaFile.media.fileName : 'Document'} </p>
                                        </a>
                                    </Desktop>
                                    {mediaFile.media.canDownload && (
                                        <ButtonIcon
                                            icon={<Download />}
                                            onClick={() => handleDownload(mediaFile.media.fileName, base64)}
                                            className={styles.DownloadButton}
                                            parent
                                        />
                                    )}
                                </div>
                            </SwiperSlide>
                        )
                    })}
                </Swiper>
            )}
        </div>
    )
}

export default MediaContainer
